import { z } from "zod";
import {
  FEEDBACK_CONTEXT_GROUPS,
  FEEDBACK_DESCRIPTION_MAX,
  FEEDBACK_TYPES,
  feedbackContextSchema,
} from "./feedback.ts";
import { agentContextSchema, type FeedbackOrigin } from "./feedback-agent-context.ts";
import { dismissalReasonSchema } from "./recommendation-outcomes.ts";

/**
 * Feedback drafts from a conversation (openspec change `feedback-from-conversation`, spec
 * `product-feedback`): the agent drafts a report with `draft_feedback`, or the user opens one
 * from an answer's "Report a problem"; only the user's click sends it. The draft's agent context
 * is built by the server from the turn in a closed vocabulary (design D4): the model role, the
 * tool names, closed error codes and the turn's outcome, never text, a title or an identifier.
 */

export const FEEDBACK_DRAFTS_PATH = "/api/feedback/drafts";

/**
 * How a draft started: the user asked the agent (`requested`), the agent noticed a system
 * failure (`noticed`), or the user's "Report a problem" on an answer (`answer_action`).
 */
export const FEEDBACK_DRAFT_TRIGGERS = ["requested", "noticed", "answer_action"] as const;
export const feedbackDraftTriggerSchema = z.enum(FEEDBACK_DRAFT_TRIGGERS);
export type FeedbackDraftTrigger = z.infer<typeof feedbackDraftTriggerSchema>;

/** The report origin a draft's trigger sends as. */
export const FEEDBACK_ORIGIN_OF_TRIGGER: Record<FeedbackDraftTrigger, FeedbackOrigin> = {
  requested: "agent_requested",
  noticed: "agent_suggested",
  answer_action: "answer_action",
};

export const FEEDBACK_DRAFT_STATUSES = ["pending", "sent", "dismissed"] as const;
export type FeedbackDraftStatus = (typeof FEEDBACK_DRAFT_STATUSES)[number];

/** An undecided draft expires after 7 days (spec "Only the user sends a draft"). */
export const FEEDBACK_DRAFT_TTL_DAYS = 7;
/** Decided drafts are kept this long (the de-duplication memory), then purged (design D9). */
export const FEEDBACK_DRAFT_RETENTION_DAYS = 30;

/** The groups a sender may untick: the menu's context groups and the Assistant group. */
export const FEEDBACK_DRAFT_OMIT_GROUPS = [...FEEDBACK_CONTEXT_GROUPS, "assistant"] as const;
export type FeedbackDraftOmitGroup = (typeof FEEDBACK_DRAFT_OMIT_GROUPS)[number];

/** A draft as its owner sees it: the card and the dialog's prefilled mode. */
export const feedbackDraftViewSchema = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  /** Null for a failed turn whose answer was never stored. */
  messageId: z.uuid().nullable(),
  trigger: feedbackDraftTriggerSchema,
  type: z.enum(FEEDBACK_TYPES),
  /** Empty for `answer_action`: the user writes it. */
  description: z.string(),
  agentContext: agentContextSchema,
  status: z.enum(FEEDBACK_DRAFT_STATUSES),
  createdAt: z.iso.datetime({ offset: true }),
});
export type FeedbackDraftView = z.infer<typeof feedbackDraftViewSchema>;

/** The `data-feedback-draft` part streamed under the answer that drafted it. */
export const FEEDBACK_DRAFT_PART = "data-feedback-draft";
export const FEEDBACK_DRAFT_PART_ID = "feedback-draft";

export const feedbackDraftListQuerySchema = z.strictObject({ conversationId: z.uuid() });
export const feedbackDraftListSchema = z.object({ drafts: z.array(feedbackDraftViewSchema) });
export type FeedbackDraftList = z.infer<typeof feedbackDraftListSchema>;

/**
 * "Report a problem" on an answer or a failed turn's notice: a draft with an empty description,
 * no model call.
 */
export const createFeedbackDraftRequestSchema = z.strictObject({
  conversationId: z.uuid(),
  /** The answer; absent for a failed turn's notice when no answer was stored. */
  messageId: z.uuid().optional(),
  /** From a failed turn's notice: the outcome reads `failed`. */
  failed: z.literal(true).optional(),
});
export type CreateFeedbackDraftRequest = z.infer<typeof createFeedbackDraftRequestSchema>;

/**
 * Send a draft: the menu context the user saw (reduced again by the server), and, from the
 * dialog, the edited type and description and the groups they unticked. The agent context is
 * never in the body: the server sends the draft's own, unless `assistant` is unticked.
 */
export const sendFeedbackDraftRequestSchema = z.strictObject({
  type: z.enum(FEEDBACK_TYPES).optional(),
  description: z.string().trim().min(1).max(FEEDBACK_DESCRIPTION_MAX).optional(),
  context: feedbackContextSchema.optional(),
  omit: z
    .array(z.enum(FEEDBACK_DRAFT_OMIT_GROUPS))
    .max(FEEDBACK_DRAFT_OMIT_GROUPS.length)
    .optional(),
});
export type SendFeedbackDraftRequest = z.infer<typeof sendFeedbackDraftRequestSchema>;

export const dismissFeedbackDraftRequestSchema = z.strictObject({
  reason: dismissalReasonSchema.optional(),
});
export type DismissFeedbackDraftRequest = z.infer<typeof dismissFeedbackDraftRequestSchema>;

/** Why `draft_feedback` showed no card, as the agent reads it (design D5, D7). */
export const FEEDBACK_DRAFT_REFUSALS = [
  "duplicate",
  "suggestions_off",
  "copies_conversation",
  "invalid",
  "unavailable",
] as const;
export type FeedbackDraftRefusal = (typeof FEEDBACK_DRAFT_REFUSALS)[number];
